import React from 'react';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';
import {
  CardStyled,
  ContentWrapper,
  ContentWrapperWithPadding,
  TitlesWrapper,
  TitleWrapper,
  SubTitleWrapper,
} from './styles';
import { Text, Heading } from '../../typography';
import { SvgChevron, SvgWrapper, SvgArrow } from '../../svg';
import { useTheme } from '../../theme';

function Title({ value, type }) {
  if (React.isValidElement(value)) {
    return value;
  }

  if (type === 1) {
    return (
      <Text type={1} light upperCase noMargin>
        {value}
      </Text>
    );
  }

  return (
    <Heading type={5} noMargin>
      {value}
    </Heading>
  );
}

Title.propTypes = {
  value: PropTypes.oneOfType([PropTypes.node, PropTypes.string]),
  type: PropTypes.number,
};

const Card = React.forwardRef(
  (
    {
      accordion,
      accordionOnClick,
      accordionState,
      title,
      titleBorder,
      titleType,
      subTitle,
      fullWidth,
      fullHeight,
      link,
      children,
      style,
      defaultOpen,
      noPadding = 'all',
      selected,
      testid,
      ...rest
    },
    ref
  ) => {
    const theme = useTheme();
    const [isOpen, setIsOpen] = React.useState(defaultOpen);

    function onClick() {
      if (accordionOnClick) return accordionOnClick();
      return setIsOpen((v) => !v);
    }

    React.useEffect(() => {
      if (accordionState != null) {
        setIsOpen(accordionState);
      }
    }, [accordionState]);

    React.useEffect(() => {
      // eslint-disable-next-line no-console
      if (accordion && link) console.warn('Accordion and Link together are not supported');
    }, [accordion, link]);

    return (
      <CardStyled
        data-testid={testid}
        ref={ref}
        style={{ ...style }}
        fullWidth={fullWidth}
        fullHeight={fullHeight}
        selected={selected}
        {...rest}
      >
        {title && (
          <TitlesWrapper
            data-testid={`${testid}-accordion`}
            onClick={accordion ? onClick : undefined}
            withBottomBorder={(accordion && isOpen) || (!accordion && titleBorder)}
            withBottomPadding={accordion || (!accordion && titleBorder)}
          >
            <TitleWrapper>
              <Title value={title} type={titleType} />
              {accordion && (
                <SvgWrapper>
                  <SvgChevron
                    direction={isOpen ? 'top' : 'bottom'}
                    stroke={theme.colors.primary20b}
                  />
                </SvgWrapper>
              )}
              {link && (
                <Link data-testid={`${testid}-link`} to={link}>
                  <SvgWrapper style={{ cursor: 'pointer' }}>
                    <SvgArrow direction="right" stroke={theme.colors.primary20b} />
                  </SvgWrapper>
                </Link>
              )}
            </TitleWrapper>
            {subTitle && (
              <SubTitleWrapper>
                <Text type={2} light noMargin style={{ opacity: 0.5 }}>
                  {subTitle}
                </Text>
              </SubTitleWrapper>
            )}
          </TitlesWrapper>
        )}
        {(isOpen || !accordion) && (
          <ContentWrapper noPadding={noPadding}>{children}</ContentWrapper>
        )}
      </CardStyled>
    );
  }
);

Card.propTypes = {
  testid: PropTypes.string,
  accordion: PropTypes.bool,
  titleBorder: PropTypes.bool,
  fullWidth: PropTypes.bool,
  fullHeight: PropTypes.bool,
  titleType: PropTypes.number,
  subTitle: PropTypes.string,
  link: PropTypes.string,
  title: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  children: PropTypes.node,
  style: PropTypes.object,
  defaultOpen: PropTypes.bool,
  noPadding: PropTypes.oneOfType([PropTypes.bool, PropTypes.string]),
  selected: PropTypes.bool,
  accordionOnClick: PropTypes.func,
  accordionState: PropTypes.bool,
};

Card.defaultProps = {
  defaultOpen: true,
  noPadding: false,
};

Card.ContentWrapper = ContentWrapperWithPadding;
export default Card;
